import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader2, X, CheckCircle, AlertCircle, ClipboardCheck } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize } from '../../../../styling/stylingHelper';
import { createSubrecipesFromTemplate, previewSubrecipesFromTemplate, SubrecipeImportApiError, SubrecipeImportPreview as ImportPreview,
  SubrecipeTemplateResult } from '../../../../services/subrecipes/subrecipesService';
import { useSubrecipeReviewStore } from '../../../../stores/appSubrecipeReviewStore';
import { useSubrecipesStore } from '../../../../stores/appSubrecipesStore';
import SubrecipeImportPreview, { initialSubrecipeImportDrafts, subrecipeImportDecisions, SubrecipeImportDraft } from './SubrecipeImportPreview';

interface UploadSubrecipeTemplateModalProps {
  restaurantId: string;
  onClose: () => void;
  onCompleted: () => void;
}

const UploadSubrecipeTemplateModal: React.FC<UploadSubrecipeTemplateModalProps> = ({
  restaurantId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setReviewReport = useSubrecipeReviewStore((s) => s.setReport);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<SubrecipeTemplateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [drafts, setDrafts] = useState<SubrecipeImportDraft[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
      setPreview(null);
      setDrafts([]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setIsProcessing(true);
    setError(null);
    setResult(null);
    try {
      if (!preview) {
        const data = await previewSubrecipesFromTemplate(restaurantId, selectedFile);
        setPreview(data);
        setDrafts(initialSubrecipeImportDrafts(data));
        return;
      }
      const reviewed = subrecipeImportDecisions(preview, drafts);
      if (!reviewed.valid) return;
      const data = await createSubrecipesFromTemplate(restaurantId, selectedFile, { sourceHash: preview.sourceHash, decisions: reviewed.decisions });
      setResult(data);
      // Park the match report so the user can walk it whenever they want — the
      // review is offered, never forced, and survives closing this modal.
      if (data.reviewReport && data.reviewReport.subrecipes.length > 0) {
        setReviewReport(data.reviewReport, data.skippedExisting);
      }
      // The management page keeps a local list; linked dishes resolve through the shared store.
      // A refresh failure must not turn successful writes into an apparently failed import.
      const subrecipes = useSubrecipesStore.getState();
      if (subrecipes.currentRestaurantId === restaurantId) {
        void subrecipes.load(restaurantId, { force: true }).catch(console.error);
      }
      onCompleted();
    } catch (err) {
      const code = err instanceof SubrecipeImportApiError ? err.code : 'import_failed';
      setError(t(`costPrice.uploadSubrecipeTemplate.errors.${code}`, { defaultValue: t('costPrice.uploadSubrecipeTemplate.genericError') }));
    } finally {
      setIsProcessing(false);
    }
  };

  const unmatchedSubrecipes = result ? [...result.created, ...(result.updated ?? [])].filter(c => c.unmatched.length > 0) : [];
  const merged = result ? [...(result.updated ?? []), ...(result.unchanged ?? [])] : [];
  const canApply = !preview || subrecipeImportDecisions(preview, drafts).valid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/60 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-background dark:bg-background-dark rounded-none sm:rounded-lg max-w-2xl w-full h-full sm:h-auto max-h-none sm:max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
            {t('costPrice.uploadSubrecipeTemplate.title')}
          </h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            aria-label={t('costPrice.uploadSubrecipeTemplate.close')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className={cn(primaryText, smallTextSize, 'opacity-70')}>
            {t('costPrice.uploadSubrecipeTemplate.subtitle')}
          </p>

          {/* File Upload */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, 'block mb-2')}>
              {t('costPrice.uploadSubrecipeTemplate.uploadLabel')}
            </label>
            <label className="flex items-center justify-center px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-colors">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                <span className={cn(primaryText, smallTextSize)}>
                  {selectedFile ? selectedFile.name : t('costPrice.uploadSubrecipeTemplate.uploadHint')}
                </span>
                <p className={cn(primaryText, 'text-xs opacity-60 mt-1')}>
                  {t('costPrice.uploadSubrecipeTemplate.fileType')}
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                disabled={isProcessing}
              />
            </label>
            {selectedFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <FileText className="h-4 w-4" />
                <span>{selectedFile.name}</span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className={cn(primaryText, 'text-sm text-red-700 dark:text-red-400')}>{error}</p>
            </div>
          )}

          {preview && !result && (
            <>
              <SubrecipeImportPreview preview={preview} drafts={drafts} onChange={setDrafts} disabled={isProcessing} />
              <button type="button" disabled={isProcessing} onClick={() => { setPreview(null); setDrafts([]); setError(null); }}
                className="text-sm text-accent dark:text-accent-dark underline">
                {t('costPrice.uploadSubrecipeTemplate.matchReview.restart')}
              </button>
            </>
          )}

          {/* Result Display */}
          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <p className={cn(primaryText, semiBoldText, smallTextSize, 'text-green-800 dark:text-green-300')}>
                  {t('costPrice.uploadSubrecipeTemplate.createdCount', { count: result.created.length })}
                </p>
              </div>

              {!!result.updated?.length && <p className={cn(primaryText, 'text-sm')}>
                {t('costPrice.uploadSubrecipeTemplate.updatedCount', { count: result.updated.length })}
              </p>}

              {!!result.unchanged?.length && <p className={cn(primaryText, 'text-sm')}>
                {t('costPrice.uploadSubrecipeTemplate.unchangedCount', { count: result.unchanged.length })}
              </p>}

              {merged.some(item => item.alreadyPresent?.length) && (
                <div className={cn(primaryText, 'text-sm space-y-1')}>
                  <p className={semiBoldText}>{t('costPrice.uploadSubrecipeTemplate.alreadyPresent')}</p>
                  <ul className="list-disc list-inside break-words">
                    {merged.filter(item => item.alreadyPresent?.length).map(item => (
                      <li key={item.id}>
                        {item.name}: {item.alreadyPresent.map(line => `${line.ingredient} → ${line.productName}`).join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!!result.failed?.length && (
                <div role="alert" className="text-sm text-red-700 dark:text-red-300 space-y-2">
                  <p>{t('costPrice.uploadSubrecipeTemplate.failedHint')}</p>
                  <ul className="list-disc list-inside">
                    {result.failed.map(item => <li key={item.index}>{item.name}: {t(`costPrice.uploadSubrecipeTemplate.errors.${item.reason}`)}</li>)}
                  </ul>
                </div>
              )}

              {result.skippedExisting.length > 0 && (
                <div className={cn(primaryText, 'text-sm')}>
                  <span className={semiBoldText}>{t('costPrice.uploadSubrecipeTemplate.skippedExisting')}:</span>{' '}
                  {result.skippedExisting.join(', ')}
                </div>
              )}

              {unmatchedSubrecipes.length > 0 && (
                <div className={cn(primaryText, 'text-sm')}>
                  <p className={semiBoldText}>{t('costPrice.uploadSubrecipeTemplate.unmatchedIngredients')}:</p>
                  <ul className="list-disc list-inside opacity-80">
                    {unmatchedSubrecipes.map((c) => (
                      <li key={c.id}>
                        {c.name}: {c.unmatched.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className={cn(primaryText, 'text-xs opacity-60')}>
                {t('costPrice.uploadSubrecipeTemplate.aiCost', { cost: result.totalCost.toFixed(2) })}
              </p>

              {result.reviewReport && result.reviewReport.subrecipes.length > 0 && (
                <button
                  onClick={() => navigate('/costprice/subrecipe-review')}
                  className="w-full px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <ClipboardCheck className="h-5 w-5" />
                  {t('costPrice.subrecipeReview.reviewResults', { count: result.reviewReport.subrecipes.length })}
                </button>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-primaryText dark:text-primaryText-dark rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {result ? t('costPrice.uploadSubrecipeTemplate.close') : t('costPrice.uploadSubrecipeTemplate.cancel')}
            </button>
            {!result && (
              <button
                onClick={handleSubmit}
                disabled={!selectedFile || isProcessing || !canApply}
                className="flex-1 px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('costPrice.uploadSubrecipeTemplate.processing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5" />
                    {t(preview ? 'costPrice.uploadSubrecipeTemplate.matchReview.confirm' : 'costPrice.uploadSubrecipeTemplate.process')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadSubrecipeTemplateModal;
